import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import RosterManualEntryForm, {
  MANUAL_ROSTER_ITEM_DEFAULT_VALUES,
  TRosterUserType,
} from '../onboarding/organization/C_RosterManualEntryForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { rosterUsersSchema } from '../onboarding/organization/validations';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import RosterCSVFile from '../onboarding/organization/C_RosterCSVFile';
import { TParsedRosterList, TPeerSupportCertificateInfoMap } from '@/store/useOrganizationOnboardingStore';
import RosterPreview from '../onboarding/organization/D_RosterPreview';
import OrgOnboardingPage from '../onboarding/organization/OrgOnboardingPage';
import onboardingMessages from '@/messages/onboarding';
import useAddMemberToOrganization from '@/hooks/organization/addMemberToOrganization';
import { TUserAffiliation, TUserInviteInfo } from '@/types/User.types';
import { getInviteFailures } from '@/utils/inviteFailures';

interface Props {
  onMemberAddFinish: () => void;
  onGoBack: () => void;
}

const trimInviteValue = (value: string | undefined) => value?.trim() ?? '';
const BULK_ADD_TITLE = 'Bulk Add Members to Your Organization';

const OrgMembersRosterBulkAdd = ({ onMemberAddFinish, onGoBack }: Props) => {
  const [step, setStep] = useState<0 | 1 | 2>(0);

  const [rosterCSVFile, setRosterCSVFile] = useState<File | null>(null);
  const [rosterCSVFileNamePreview, setRosterCSVFileNamePreview] = useState<string | null>(null);
  const [rosterList, setRosterList] = useState<TParsedRosterList>([]);
  const [customRosterError, setCustomRosterError] = useState('');

  const [peerSupportCertificateDataCSV, setPeerSupportCertificateDataCSV] = useState<TPeerSupportCertificateInfoMap>(
    new Map(),
  );

  const methodsCSV = useForm<{
    rosterUsers?: TRosterUserType[];
  }>({
    defaultValues: {
      rosterUsers: rosterList ? rosterList : [MANUAL_ROSTER_ITEM_DEFAULT_VALUES],
    },
    mode: 'all',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(rosterUsersSchema),
  });

  const {
    isPending: isInvitePending,
    mutate: inviteMembersToOrg,
    data,
    error,
    reset,
  } = useAddMemberToOrganization(onMemberAddFinish);
  const failed = data?.failed ?? getInviteFailures(error);

  const openEditableRoster = useCallback(async () => {
    methodsCSV.reset({ rosterUsers: rosterList ?? [] });
    setStep(1);
    await methodsCSV.trigger('rosterUsers');
  }, [methodsCSV, rosterList]);

  const saveEditedRoster = useCallback(() => {
    setRosterList(
      (methodsCSV.getValues('rosterUsers') ?? []).map((user) => ({
        ...user,
        Affiliation: user.Affiliation as TUserAffiliation,
        Invitation: '',
      })),
    );
  }, [methodsCSV]);

  const previewEditedRoster = useCallback(async () => {
    if (!(await methodsCSV.trigger('rosterUsers'))) return;

    saveEditedRoster();
    setStep(2);
  }, [methodsCSV, saveEditedRoster]);

  const submitNewMembers = useCallback(async () => {
    const usersToInvite: TUserInviteInfo[] =
      rosterList?.map((rosterItem) => ({
        email: trimInviteValue(rosterItem.Email).toLowerCase(),
        firstName: trimInviteValue(rosterItem['First Name']),
        lastName: trimInviteValue(rosterItem['Last Name']),
        userAffiliation: trimInviteValue(rosterItem.Affiliation).toUpperCase(),
        phone: trimInviteValue(rosterItem.Phone),
        isPeerSupport: rosterItem['Peer Support'],
      })) ?? [];

    reset();
    inviteMembersToOrg(usersToInvite);
  }, [rosterList, inviteMembersToOrg, reset]);

  if (step === 2) {
    return (
      <RosterPreview
        rosterList={rosterList}
        onGoBack={() => {
          reset();
          setStep(1);
        }}
        onSubmit={failed.length > 0 ? onMemberAddFinish : submitNewMembers}
        isLoading={isInvitePending}
        isDisabledSubmit={isInvitePending}
        failedRows={failed}
        invited={data?.invited}
        showInvitedOnly={failed.length > 0 && data !== undefined}
        headerHidden={true}
      />
    );
  }

  if (step === 1) {
    return (
      <OrgOnboardingPage
        title={BULK_ADD_TITLE}
        subTitle="Review your uploaded roster and edit member details below."
        description="Correct any missing or misformatted information before previewing the roster and inviting these members."
        onGoBack={() => {
          saveEditedRoster();
          setStep(0);
        }}
        onSubmit={() => void previewEditedRoster()}
        isSubmitDisabled={!methodsCSV.formState.isValid}
        headerHidden={true}
      >
        <RosterManualEntryForm
          methods={methodsCSV}
          peerSupportData={peerSupportCertificateDataCSV}
          setPeerSupportData={setPeerSupportCertificateDataCSV}
          errorDisplay="both"
        />
      </OrgOnboardingPage>
    );
  }

  return (
    <VStack paddingBottom={'40px'}>
      <Text variant={'loraSmallTitle'}>{BULK_ADD_TITLE}</Text>
      <RosterCSVFile
        csvTabStep={0}
        selectedFile={rosterCSVFile}
        setSelectedFile={(file) => {
          reset();
          setRosterCSVFile(file);
        }}
        preview={rosterCSVFileNamePreview}
        setPreview={setRosterCSVFileNamePreview}
        setRoster={setRosterList}
        setCustomRosterError={setCustomRosterError}
        methods={methodsCSV}
        peerSupportData={peerSupportCertificateDataCSV}
        setPeerSupportData={setPeerSupportCertificateDataCSV}
        titleText={onboardingMessages.uploadUpdatedToYourRoster}
        descriptionText={onboardingMessages.uploadYourUpdatedRosterDescription}
        buttonsColor="primary"
      />

      <Text variant={'error'} marginTop={'10px'}>
        {customRosterError}
      </Text>

      <HStack width={'100%'} bg="background.separatorBlue" height={'2px'} marginTop={'54px'} marginBottom={'72px'} />

      <Button
        variant={'continue'}
        disabled={!rosterCSVFileNamePreview}
        isDisabled={!rosterCSVFileNamePreview}
        _hover={{ backgroundColor: !rosterCSVFileNamePreview ? 'primary.400' : 'primary.600' }}
        onClick={() => void openEditableRoster()}
      >
        Continue
      </Button>
      <Text
        onClick={onGoBack}
        cursor={'pointer'}
        marginY={'40px'}
        fontWeight={700}
        fontSize={'16px'}
        color={'text.mediumGray'}
        lineHeight={'18px'}
      >
        Go Back
      </Text>
    </VStack>
  );
};

export default OrgMembersRosterBulkAdd;
